import type { DatedEvent } from '@/store/events/events.dto';
import type { PersonPresenceStatus } from '@/store/persons/person.dto';
import { getActiveDashboardType } from '@/utils/dashboard.types';
import { UserRole } from '@/utils/roles';

export type ViewRole = 'owner' | 'captain' | 'crew' | 'guest' | 'vendor';
export type Presence = 'on-board' | 'ashore';

export interface Person {
  id: string;
  firstName: string;
  lastName: string;
  role: string;
  presence: Presence;
  avatar?: string;
}

export interface EventOption {
  id: string;
  name: string;
  date: string;
  time: string;
}

export const PRESENCE_LABEL: Record<Presence, string> = {
  'on-board': 'Aboard',
  ashore: 'Ashore',
};

export const PRESENCE_LABEL_SHORT: Record<Presence, string> = {
  'on-board': 'On',
  ashore: 'Off',
};

const ROLE_LABELS: Record<string, string> = {
  [UserRole.OWNER]: 'Owner',
  [UserRole.CO_OWNER]: 'Co-owner',
  [UserRole.CAPTAIN]: 'Captain',
  [UserRole.ENGINEER]: 'Engineer',
  [UserRole.MATE_DECKHAND]: 'Mate',
  [UserRole.CHEF]: 'Chef',
  [UserRole.STEWARD]: 'Steward',
  [UserRole.CREW_MEMBER]: 'Crew',
  [UserRole.GUEST]: 'Guest',
  [UserRole.SERVICE_PROVIDER]: 'Service Provider',
};

export const formatRole = (role: string): string =>
  ROLE_LABELS[role] ?? (role ? role.charAt(0).toUpperCase() + role.slice(1) : '');

export const toPresence = (status?: PersonPresenceStatus): Presence =>
  status === 'on_board' ? 'on-board' : 'ashore';

export const toBackendPresence = (presence: Presence): PersonPresenceStatus =>
  presence === 'on-board' ? 'on_board' : 'off_board';

const DASHBOARD_TO_VIEW_ROLE: Record<string, ViewRole> = {
  owner: 'owner',
  captain: 'captain',
  crew: 'crew',
  guest: 'guest',
  service_provider: 'vendor',
  no_role: 'crew',
};

export const deriveViewRole = (userRoles: readonly UserRole[]): ViewRole =>
  DASHBOARD_TO_VIEW_ROLE[getActiveDashboardType([...userRoles])] ?? 'crew';

const timeOf = (date: Date): string =>
  date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

export const mapEventToOption = (event: DatedEvent): EventOption => {
  const start = new Date(event.time_slot.start);
  const end = new Date(event.time_slot.end);
  return {
    id: event.id,
    name: event.title,
    date: start.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }),
    time: `${timeOf(start)} – ${timeOf(end)}`,
  };
};

export const shortEventLine = (ev: EventOption): string => {
  const dateShort = ev.date.split(',')[0];
  const timeStart = ev.time.split('–')[0].trim();
  return `${ev.name} · ${dateShort} · ${timeStart}`;
};
